import classnames from 'classnames';
import * as React from 'react';
import { type ComponentType, forwardRef, type ReactNode, type RefObject } from 'react';
import { useInView } from 'react-intersection-observer';

import { mergeRefs } from '../../lib/ref';

import styles from './withVisibilityFade.module.css';

const MIN_INTERSECTION_RATIO = 0.9;
const MIN_OPACITY = 0.2;
const CAROUSEL_ITEM_VISIBILITY_THRESHOLDS = [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1];

interface CarouselSlideProps {
    scrollRef?: RefObject<HTMLElement>;
    className?: string;
    minIntersectionRatio?: number;
    children: ReactNode;
    [key: string]: any;
}

const getOpacityClassName = (
    intersectionRatio: number,
    minIntersectionRatio: number = MIN_INTERSECTION_RATIO
): string | undefined => {
    if (intersectionRatio < minIntersectionRatio) {
        const opacity = MIN_OPACITY + (1 - MIN_OPACITY) * intersectionRatio;

        return `opacity-${Math.round(opacity * 10) * 10}`;
    }
    return undefined;
};

/**
 * Higher-order component that adds fade-in/out visibility effects to a carousel slide component.
 * Uses IntersectionObserver to track visibility and applies opacity classes based on intersection ratio.
 *
 * @param Component - The React component to be enhanced with visibility fade effects
 * @returns A new component that wraps the original with visibility fade behavior
 *
 * @example
 * const FadeSlide = withVisibilityFade(CarouselSlide);
 *
 * // Then use FadeSlide in your carousel
 * <FadeSlide key={item.id} scrollRef={scrollRef}>
 *   {item.content}
 * </FadeSlide>
 */
export const withVisibilityFade = (Component: ComponentType<CarouselSlideProps>) =>
    forwardRef<HTMLLIElement, CarouselSlideProps>(
        (
            {
                scrollRef,
                className,
                minIntersectionRatio = MIN_INTERSECTION_RATIO,
                children,
                ...restProps
            },
            ref
        ) => {
            const { ref: inViewRef, entry } = useInView({
                root: scrollRef?.current || null,
                threshold: CAROUSEL_ITEM_VISIBILITY_THRESHOLDS,
            });

            const intersectionRatio = entry?.intersectionRatio ?? 0;

            const opacityClassName = getOpacityClassName(intersectionRatio, minIntersectionRatio);

            return (
                <Component
                    className={classnames(
                        styles.root,
                        opacityClassName && styles[opacityClassName],
                        className
                    )}
                    ref={mergeRefs(ref, inViewRef)}
                    {...restProps}
                >
                    {children}
                </Component>
            );
        }
    );
